// client/src/components/common/UpdateAvailableBanner.jsx
//
// "A new version is available" prompt. serviceWorkerRegistration's onUpdate
// fires when a new service worker has installed but is still waiting behind
// the old one — without this, the user keeps running the old bundle until
// every tab is closed (a real problem for door staff mid-event).
//
// index.js calls announceUpdate(registration); this banner listens for it.
// "Refresh" tells the waiting worker to skipWaiting (handled in
// service-worker.js) and reloads once it has taken control.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const SW_UPDATE_EVENT = 'onscreen:sw-update';

export function announceUpdate(registration) {
  window.__onscreenSwUpdate = registration;
  window.dispatchEvent(new CustomEvent(SW_UPDATE_EVENT, { detail: registration }));
}

export default function UpdateAvailableBanner() {
  const { t } = useTranslation();
  // Pick up an update announced before this component mounted.
  const [registration, setRegistration] = useState(() => window.__onscreenSwUpdate || null);
  const [dismissed, setDismissed] = useState(false);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    const onUpdate = (e) => {
      setRegistration(e.detail);
      setDismissed(false);
    };
    window.addEventListener(SW_UPDATE_EVENT, onUpdate);
    return () => window.removeEventListener(SW_UPDATE_EVENT, onUpdate);
  }, []);

  if (!registration || dismissed) return null;

  const refresh = () => {
    setReloading(true);
    const waiting = registration.waiting;
    if (!waiting || !navigator.serviceWorker) {
      window.location.reload();
      return;
    }
    let reloaded = false;
    const reloadOnce = () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', reloadOnce);
    waiting.postMessage({ type: 'SKIP_WAITING' });
    // Fallback in case controllerchange never fires (e.g. worker already redundant).
    setTimeout(reloadOnce, 4000);
  };

  return (
    <div className="update-banner note info" role="status" aria-live="polite" data-testid="update-banner">
      <span className="update-banner__text">
        {t('pwa.updateAvailable', 'A new version of On-Screen is available.')}
      </span>
      <div className="update-banner__actions">
        <button type="button" className="update-banner__refresh" onClick={refresh} disabled={reloading}>
          {reloading ? t('pwa.updating', 'Updating…') : t('pwa.refresh', 'Refresh')}
        </button>
        <button
          type="button"
          className="update-banner__later"
          onClick={() => setDismissed(true)}
          disabled={reloading}
        >
          {t('pwa.later', 'Later')}
        </button>
      </div>
    </div>
  );
}
